/**
 * Resolves a single stable identifier for the current visitor. Prefers
 * Microsoft Clarity's own id (its `_clck` cookie — Clarity is already loaded
 * on every page via the inline snippet in public/index.html) so this
 * device/browser can be cross-referenced with a Clarity session recording —
 * but works standalone too: falls back to a locally generated,
 * localStorage-persisted id when Clarity hasn't set its cookie yet (e.g. the
 * very first request of a visit) or is blocked, so callers always get
 * *something* stable rather than null.
 */

const FALLBACK_ID_KEY = "iq_user_id";

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Clarity's `_clck` cookie is formatted "<clarityUserId>|<year>|<count>|<...>".
 * The first segment is the stable per-browser Clarity user id (distinct from
 * `_clsk`, which is per-session).
 */
export function getClarityId() {
  const clck = readCookie("_clck");
  if (!clck) return null;
  const id = clck.split("|")[0];
  return id || null;
}

function getOrCreateFallbackId() {
  try {
    let id = window.localStorage.getItem(FALLBACK_ID_KEY);
    if (!id) {
      id = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      window.localStorage.setItem(FALLBACK_ID_KEY, id);
    }
    return id;
  } catch {
    return "no-local-storage";
  }
}

/**
 * The identifier to use as "the" per-user id everywhere (logs). Re-reads the
 * cookie on every call (cheap) so that once Clarity sets `_clck` this
 * automatically starts picking up the real id instead of the fallback, with
 * no code changes needed here.
 */
export function getUserIdentifier() {
  return getClarityId() || getOrCreateFallbackId();
}
